import React, { useState, useEffect } from 'react';
import { Calendar, CircleDot, Wallet, Lock, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/apiClient';
import { formatDate, formatTime } from '../utils/dateUtils';

function MatchList() {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/matches');

        // Sort matches by kickoff time (newest first)
        const sortedMatches = response.data.sort((a, b) =>
          new Date(a.kickoff_time) - new Date(b.kickoff_time)
        );

        setMatches(sortedMatches);
        setError(null);
      } catch (err) {
        setError('Erro ao carregar partidas');
        console.error('Error fetching matches:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, []);

  if (loading) {
    return <div>Carregando partidas...</div>;
  }

  if (error) {
    return <div className="alert alert-error">{error}</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Header Section — clicável para colapsar */}
      <button
        onClick={() => setIsOpen(prev => !prev)}
        className="w-full bg-gradient-to-r from-primary to-primary-dark p-6 text-white flex items-center justify-between focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        aria-expanded={isOpen}
      >
        <h2 className="text-2xl font-bold flex items-center">
          <Calendar className="w-6 h-6 mr-2 flex-shrink-0" aria-hidden="true" />
          Partidas da Copa
          {matches.length > 0 && (
            <span className="ml-3 text-sm font-normal opacity-75">({matches.length} partidas)</span>
          )}
        </h2>
        {isOpen
          ? <ChevronUp className="w-6 h-6 flex-shrink-0" aria-hidden="true" />
          : <ChevronDown className="w-6 h-6 flex-shrink-0" aria-hidden="true" />
        }
      </button>

      {/* Collapsible + scrollable content */}
      <div className={`transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[520px] overflow-y-auto' : 'max-h-0 overflow-hidden'}`}>

      {/* Empty State */}
      {matches.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4"><CircleDot className="w-12 h-12 mx-auto text-primary-dark" aria-label="Bola" /></div>
          <p className="text-xl text-gray-600">Nenhuma partida encontrada</p>
        </div>
      ) : (
        /* Match List */
        <div className="divide-y divide-green-100">
          {matches.map((match) => (
            <div
              key={match.id}
              className="p-6 hover:bg-yellow-50 transition-colors duration-200"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                {/* Teams and Match Info */}
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-6">
                    {/* Home Team */}
                    <div className="text-center sm:text-left min-w-0">
                      <div className="text-xl font-bold text-green-900 break-words" title={match.home_team_pt || match.home_team}>
                        {match.home_team_pt || match.home_team}
                      </div>
                    </div>

                    {/* VS Divider */}
                    <div className="text-2xl font-bold text-gray-500">VS</div>

                    {/* Away Team */}
                    <div className="text-center sm:text-right min-w-0">
                      <div className="text-xl font-bold text-green-900 break-words" title={match.away_team_pt || match.away_team}>
                        {match.away_team_pt || match.away_team}
                      </div>
                    </div>
                  </div>

                  {/* Match Details */}
                  <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-gray-600">
                    {/* Date */}
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                      {formatDate(match.kickoff_time)}
                    </span>

                    {/* Time */}
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      {formatTime(match.kickoff_time)}
                    </span>

                    {/* Group Stage Badge */}
                    {match.group_stage && (
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                        Grupo {match.group_stage}
                      </span>
                    )}

                    {/* Round Badge */}
                    {match.round && (
                      <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                        {match.round}
                      </span>
                    )}
                  </div>
                </div>

                {/* Action/Status Section */}
                <div className="flex flex-col items-center sm:items-end space-y-2">
                  {/* Completed Match with Score */}
                  {match.status === 'completed' ? (
                    <div className="flex flex-col items-center">
                      <div className="text-3xl font-bold text-primary-dark flex items-center gap-4">
                        <span className="bg-green-100 px-3 py-1 rounded-lg">
                          {match.home_score}
                        </span>
                        <span>-</span>
                        <span className="bg-green-100 px-3 py-1 rounded-lg">
                          {match.away_score}
                        </span>
                      </div>
                      {match.winner_on_draw && (
                        <span className="text-xs text-orange-600 font-medium mt-1">
                          {match.winner_on_draw === 'home'
                            ? (match.home_team_pt || match.home_team)
                            : (match.away_team_pt || match.away_team)} avança nos pênaltis
                        </span>
                      )}
                    </div>
                  ) :

                  /* Authenticated User with Verified Payment */
                  user && user.payment_verified ? (
                    <a
                      href="/predictions"
                      className="bg-primary hover:bg-primary-dark text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center space-x-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                      </svg>
                      <span>Fazer Palpite</span>
                    </a>
                  ) :

                  /* Authenticated User without Verified Payment */
                  user && !user.payment_verified ? (
                    <div className="text-center">
                      <div className="text-yellow-700 text-sm font-medium bg-yellow-100 px-3 py-2 rounded-lg">
                        <Wallet className="w-4 h-4 inline mr-1" aria-hidden="true" />Verifique seu pagamento
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        para fazer palpites
                      </div>
                    </div>
                  ) :

                  /* Public User (Not Logged In) */
                  (
                    <div className="text-center">
                      <div className="text-gray-600 text-sm font-medium bg-gray-100 px-3 py-2 rounded-lg">
                        <Lock className="w-4 h-4 inline mr-1" aria-hidden="true" />Faça login
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        para fazer palpites
                      </div>
                    </div>
                  )}

                  {/* Result Confirmation Badge */}
                  {match.result_confirmed && (
                    <div className="text-xs text-primary-dark font-medium flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      Resultado confirmado
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      </div>{/* fim collapsible */}
    </div>
  );
}

export default MatchList;